import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Dropdown,
  Modal,
} from "react-bootstrap";
import { TiTickOutline } from "react-icons/ti";
import { IoEye } from "react-icons/io5";
import "./approved-leave.css";
import LeaveLayout from "../../components/LeaveLayout.jsx";

export default function ApprovedLeave() {
  const [showModal, setShowModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return isNaN(date) ? "-" : dateFormatter.format(date);
  };

  // Fetch Approved Leaves
  const fetchApprovedLeaves = async (searchQuery = "", page = 1, limit = 15) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `https://backend.ems.hiradentallab.com/api/leavesmanual/approved-leaves?search=${encodeURIComponent(searchQuery)}&page=${page}&limit=${limit}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Support both "approvedLeaves" and "leaves" key (backend old/new)
        const fetchedRows = data.approvedLeaves || data.leaves || [];
        setRows(fetchedRows);
        setTotalEntries(data.totalEntries || fetchedRows.length);
      } else {
        setRows([]);
        setTotalEntries(0);
        setError(data.message || "Failed to fetch approved leaves");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message);
      setRows([]);
      setTotalEntries(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search + Pagination
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchApprovedLeaves(searchTerm, currentPage, rowsPerPage);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, currentPage, rowsPerPage]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > Math.ceil(totalEntries / rowsPerPage)) return;
    setCurrentPage(pageNumber);
  };

  const handleShowModal = (rowData) => {
    setSelectedRowData(rowData);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRowData(null);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalEntries / rowsPerPage);

  if (loading && rows.length === 0) {
    return (
      <LeaveLayout>
        <div className="loading">Loading approved leaves...</div>
      </LeaveLayout>
    );
  }

  if (error && rows.length === 0) {
    return (
      <LeaveLayout>
        <div className="error">Error: {error}</div>
      </LeaveLayout>
    );
  }

  return (
    <LeaveLayout>
      <div>
        <h1 className="approved-leave-header">
          <TiTickOutline className="icon-tick" /> Approved Leave
        </h1>
        <hr />

        <div className="approved-leave-content">
          <div className="filter-container">
            <Col xs={12} md={6} className="filter-left">
              <Dropdown className="d-inline mx-2">
                <Dropdown.Toggle id="dropdown-autoclose-inside">
                  Show Entries: {rowsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[15, 25, 50].map((num) => (
                    <Dropdown.Item
                      key={num}
                      onClick={() => {
                        setRowsPerPage(num);
                        setCurrentPage(1);
                      }}
                    >
                      {num}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={12} md={6} className="filter-right">
              <Form>
                <Form.Control
                  type="search"
                  placeholder="Search by name, reason, type..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Form>
            </Col>
          </div>

          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="approved-leave-table-header">
                <tr>
                  <th>SR</th>
                  <th>Name</th>
                  <th>Leave Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Applied Date</th>
                  <th>HR Approval</th>
                  <th>Admin Approval</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((row, index) => (
                    <tr key={row._id || row.id || index}>
                      <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                      <td>{row.name || "-"}</td>
                      <td>{row.leaveType || row.allowanceType || "-"}</td>
                      <td>{formatDate(row.startDate)}</td>
                      <td>{formatDate(row.endDate)}</td>
                      <td>{formatDate(row.appliedDate || row.createdAt)}</td>
                      <td>{row.hrAction || row.hrApproval || "-"}</td>
                      <td>{row.adminAction || row.adminApproval || "-"}</td>
                      <td>{row.status || "Approved"}</td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleShowModal(row)}
                        >
                          <IoEye />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      No approved leaves found.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="table-footer">
                  <td><strong>Total</strong></td>
                  <td>-</td>
                  <td>{totalEntries}</td>
                  <td colSpan="7"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-row d-flex justify-content-between align-items-center mt-3">
            <div className="entries-info">
              Showing{" "}
              {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, totalEntries)} of{" "}
              {totalEntries} entries
            </div>

            <nav>
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <a
                    href="#"
                    className="page-link"
                    onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}
                  >
                    Previous
                  </a>
                </li>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, currentPage - 2) + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${currentPage === pageNum ? "active" : ""}`}
                    >
                      <a
                        href="#"
                        className="page-link"
                        onClick={(e) => { e.preventDefault(); handlePageChange(pageNum); }}
                      >
                        {pageNum}
                      </a>
                    </li>
                  );
                })}

                <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
                  <a
                    href="#"
                    className="page-link"
                    onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}
                  >
                    Next
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        {/* Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Leave Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRowData && (
              <>
                <p><strong>Name:</strong> {selectedRowData.name}</p>
                <p><strong>Leave Type:</strong> {selectedRowData.leaveType || selectedRowData.allowanceType}</p>
                <p><strong>Start Date:</strong> {formatDate(selectedRowData.startDate)}</p>
                <p><strong>End Date:</strong> {formatDate(selectedRowData.endDate)}</p>
                <p><strong>Applied Duration:</strong> {selectedRowData.appliedDuration || "-"} day(s)</p>
                <p><strong>Applied Date:</strong> {formatDate(selectedRowData.appliedDate)}</p>
                <p><strong>HR Approval:</strong> {selectedRowData.hrApproval || selectedRowData.hrAction || "-"}</p>
                <p><strong>Admin Approval:</strong> {selectedRowData.adminApproval || selectedRowData.adminAction || "-"}</p>
                <p><strong>Status:</strong> {selectedRowData.status}</p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </LeaveLayout>
  );
}